import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useCallback, useState } from "react";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { evaluateParamValue } from "../../../../common/utils/params_utils";

import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";
import { useNavigate } from "react-router";
import { CentroCustoDropDown } from "../../../../common/domain/models/centro_custo_dropdown";
import { useDropDownLoader } from "../../../../common/hooks/use_dropdown_loader";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { DropDown } from "../../../../common/domain/models/dropdown";
import { ControleEstoqueForm, controleEstoqueSchema } from "../schemas/Controle_Estoque/controle_estoque";
import { useControleEstoqueFilters } from "../hooks/controleEstoque_lista/filter";
import { useControleEstoquePagination } from "../hooks/controleEstoque_lista/pagination";
import { ControleEstoque } from "../../domain/models/controle_estoque_lista";

type LoadEstoqueListaFuncState = {
  IdControleEstoque?: number;
  DescricaoPesquisa?: string;
  centroDeCusto?: number[],
  centroDeCustoLista?: CentroCustoDropDown[] | undefined,
  page?: number;
  rowsPerPage?: number;
};

export const useControleEstoqueListaViewModel = () => {
  const {
    commons: { getAllLogAlteracaoUseCase, getAllLogAlteracaoByParentUseCase},
    reports: {getReportPlanoAuditoriaUseCase},
    controleEstoque: { getAllListagemControleUseCase, deleteControleEstoqueUseCase, createControleUseCase, updateControleUseCase },
  } = useDependencyInjection();

  const {
    loadCentroCustoDropDown,
  } = useDropDownLoader();

  const hookForm = useForm<ControleEstoqueForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(controleEstoqueSchema),
    defaultValues: {
      
    }
  });
  //#region Relatorio
  const [reportViewerState, startReportViewerProcess] = useReportViewer();
  //#endregion
  const changeHistory = useChangeHistory({
    getAllLogAlteracaoByParentUseCase: getAllLogAlteracaoByParentUseCase,
    getAllLogAlteracaoUseCase: getAllLogAlteracaoUseCase,
    onSetError: (e) => pushErrorNotification("Erro", [e]),
  });
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [controleEstoqueModal, setControleEstoqueModal] = useState<boolean>(false);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [controleEstoqueLista, setControleEstoqueLista] = useState<ControleEstoque[]>([]);
  const [selectedControleEstoque, setSelectedControleEstoque] = useState<ControleEstoque>();
  const [centroDeCustoLista, setCentroDeCustoLista] = useState<CentroCustoDropDown[]>([])
  const [selectedProdutosServicosQuery, setSelectedProdutosServicosQuery] = useState<DropDown[] | null>(null);

  const {pushErrorNotification, setCustomLoading, pushInfoNotification, pushWarnNotification } = usePageLoader();
  const debouncedRef = useCreateDebounceHandler(750);

  const filters = useControleEstoqueFilters();
  const pagination = useControleEstoquePagination();
  

  // #region Funções de carregamento

  const loadEstoqueListaState = useCallback(async (funcState?: LoadEstoqueListaFuncState) => {
      try {
        const filterID_ControleEstoque = evaluateParamValue(funcState?.IdControleEstoque, filters.IdControleEstoque);
        const filteredPesquisa = funcState?.DescricaoPesquisa ?? filters.DescricaoPesquisa;
        const filteredCentroDeCusto = funcState?.centroDeCusto ?? filters.centroDeCusto;
        const filteredPage = funcState?.page ?? pagination.page;
        const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;
        await getAllListagemControleUseCase.getAllListagemControle({
              IdControleEstoque: filterID_ControleEstoque, centroDeCusto: filteredCentroDeCusto, pesquisa: String(filteredPesquisa ),
            },
            filteredPage,
            filteredRowsPerPage
        );
        setControleEstoqueLista(getAllListagemControleUseCase.items);
        pagination.setRowsPerPage(getAllListagemControleUseCase.pageSize);
        pagination.setTotalItems(getAllListagemControleUseCase.totalItems);
      } catch (e: unknown) {
          throw new Error("Falha ao buscar controles de estoque!");
      }
  }, [filters.DescricaoPesquisa, filters.IdControleEstoque, filters.centroDeCusto, getAllListagemControleUseCase, pagination]);
  
 

  //#endregion

  // #region useEffect
  usePageStartBootstrap(async () => {
    try {
      const jobs = [];

      jobs.push(loadEstoqueListaState());
       await loadCentroCustoDropDown(list => setCentroDeCustoLista( list ));
      jobs.push(loadCentroCustoDropDown(list => useControleEstoqueFilters.setState({ centroDeCustoLista: list })));
      jobs.push(loadCentroCustoDropDown(list => setCentroDeCustoLista( list )));
      await Promise.all(jobs);
    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao buscar dados da pagina!')[0]);
    }
  });

  const handleReload = useCallback(async () => {
    try {
      const jobs = [];
      setLoading(true);
      jobs.push(loadEstoqueListaState());
       await loadCentroCustoDropDown(list => setCentroDeCustoLista( list ));
      jobs.push(loadCentroCustoDropDown(list => useControleEstoqueFilters.setState({ centroDeCustoLista: list })));
      jobs.push(loadCentroCustoDropDown(list => setCentroDeCustoLista( list )));
      
      await Promise.all(jobs);
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [loadCentroCustoDropDown, loadEstoqueListaState, pushErrorNotification]);
  //#endregion

  // #region Eventos
  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      pagination.setPage(page + 1);
      await loadEstoqueListaState({page: page + 1,});
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [loadEstoqueListaState, pagination, pushErrorNotification]);

  
  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
          setLoading(true);
          pagination.setPage(1);
          pagination.setRowsPerPage(rowsPerPage);
    
          await loadEstoqueListaState({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [loadEstoqueListaState, pagination, pushErrorNotification]);


  const handleCadastroControleEstoqueOpenState = useCallback((id: number | null , entradaModal: boolean) => {
    if(id){
      setSelectedControleEstoque(controleEstoqueLista.find(x => x.IdControleEstoque === id));
      setControleEstoqueModal(entradaModal);
    }
    else{
      setControleEstoqueModal(entradaModal);
      setSelectedControleEstoque(undefined);
    } 
  }, [controleEstoqueLista]);
 
  const handleDeleteModalOpenState = useCallback((Id: number | null, openState: boolean) => {
    if (openState) {
      if (Id && controleEstoqueLista) {
        setSelectedControleEstoque(controleEstoqueLista.find(x => x.IdControleEstoque === Id));
      } else {
        setSelectedControleEstoque(undefined);
      }
    }
    setDeleteModal(openState);
  }, [controleEstoqueLista]);

  const handleDeleteConfirma = useCallback(async () => {
    if (selectedControleEstoque) {
      try {
        setSaving(true);
        await deleteControleEstoqueUseCase.deleteControleEstoque(selectedControleEstoque.IdControleEstoque);
        await loadEstoqueListaState();
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao excluir produto!'));
      } finally {
        setSaving(false);
        setDeleteModal(false);
      }
    }
  }, [deleteControleEstoqueUseCase, loadEstoqueListaState, pushErrorNotification, selectedControleEstoque]);

  const handleControleEstoqueSubmit = useCallback(async (form: ControleEstoqueForm) => {
    try {
      if(selectedControleEstoque){
        await updateControleUseCase.updateControle(selectedControleEstoque.IdControleEstoque, form.IdCentroCusto ?? null, form.Descricao ?? null);
        pushInfoNotification("Sucesso", "Atualizado com sucesso!", { isAutoFinish: true });
        setControleEstoqueModal(false);
        await loadEstoqueListaState();

      }else{
       await createControleUseCase.createControle(form.IdCentroCusto ?? null, form.Descricao ?? null);
        pushInfoNotification("Sucesso", "Criado com sucesso!", { isAutoFinish: true });
        setControleEstoqueModal(false);
        await loadEstoqueListaState();
      }
    } catch (e) {
        pushErrorNotification("Erro", processError(e, 'Falha ao criar/atualizar Controle Estoque!'));
    }finally {
      setSaving(false);
    }
  },[createControleUseCase, loadEstoqueListaState, pushErrorNotification, pushInfoNotification, selectedControleEstoque, updateControleUseCase]);

  //#endregion


  //#region Filtros
  const handleClearFilters = useCallback(async () => {
      try {
          setLoading(true);
          filters.clearFilters();
          pagination.setPage(1);
          await loadEstoqueListaState();
      } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, 'Falha ao buscar produtos!'));
      } finally {
          setLoading(false);
      }
  }, [filters, loadEstoqueListaState, pagination, pushErrorNotification]);

  const handlePesquisaChange = useDebouncedCallback( debouncedRef,  (newValue: string | null) => {
    if (newValue) {
        useControleEstoqueFilters.getState().setDescricaoPesquisa(newValue);
        return async () => {
          try {
            setLoading(true);
            useControleEstoquePagination.getState().setPage(1);
            await loadEstoqueListaState({ DescricaoPesquisa: newValue, page: 1 });
          } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao carregar pesquisar!"));
          } finally {
            setLoading(false);
          }
        }
      } else {
        return async () => {
          try {
            setLoading(true);
            useControleEstoquePagination.getState().setPage(1);
            await loadEstoqueListaState(); 
          } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao carregar pesquisa!"));
          } finally {
            setLoading(false);
          }
        }
      }
    }, 
    [ loadEstoqueListaState, pushErrorNotification, setLoading]
  );


  const handleCentroDeCustoChange = useDebouncedCallback(debouncedRef, (newValue: number[]) => {
    useControleEstoqueFilters.getState().setCentroDeCusto(newValue);
    return async () => {
      try {
        setLoading(true);
        useControleEstoquePagination.getState().setPage(1);
        await loadEstoqueListaState({ centroDeCusto: newValue, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar !"));
      } finally {
        setLoading(false);
      }
    }
  }, [ ]);

  //#endregion

  
  //#region Relatoria
  const handleAuditoriaExportar = useCallback(async (IdAuditoria: number) => {
      try {
        setLoading(true);
        // await startReportViewerProcess({
        //   fetchCallback: async (requiredMimeType) =>
        //     await getReportPlanoAuditoriaUseCase.getReportPlanoAuditoria(IdAuditoria, requiredMimeType),
        // });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao gerar relatório de auditorias!'));
      } finally {
        setLoading(false);
        setCustomLoading(false);
      }
    }, [pushErrorNotification, setCustomLoading]);


  const handleOpenChangeHistory = useCallback(
    (idTabela: number | null, tipoTabela: LogTipoTabela | null) => {
      if (idTabela && tipoTabela) {
        changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
      } 
    },
    [ changeHistory],
  );

  //#endregion
  
  return {
    loading,
    saving,
    errorMessage,
    controleEstoqueLista,
    selectedControleEstoque,
    reportViewerState,
    pagination,
    filters,
    controleEstoqueModal,
    changeHistory,
    centroDeCustoLista,
    hookForm,
    deleteModal,
    selectedProdutosServicosQuery,
    setControleEstoqueModal,
    handleReload,
    handleRowsPerPageChange,
    handlePageChange,
    handleDeleteConfirma,
    handleDeleteModalOpenState,
    handleClearFilters,
    handleAuditoriaExportar,
    handleCentroDeCustoChange,
    handlePesquisaChange,
    handleOpenChangeHistory,
    handleCadastroControleEstoqueOpenState,
    handleControleEstoqueSubmit
      
  }
}